"use client";

import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSession } from "next-auth/react";
import {
    fetchMultipleUserXpSummary,
    transformXpDataForChart,
    transformXpDataForAllTime,
    calculateTotalXp,
} from "@/app/api/xp/utils";
import XpLineChart from "../XpLineChart/XpLineChart";
import XpSummaryCard from "../XpSummaryCard/XpSummaryCard";
import { useLocale, useTranslations } from "next-intl";
import { readLocal, writeLocal } from "@/helpers/safeStorage";
import moment from "moment";

/**
 * Composant principal pour afficher le dashboard d'XP quotidien
 * @param {Array} users - Tableau d'IDs d'utilisateurs à afficher
 * @param {string} className - Classes CSS supplémentaires
 * @param {string} userTimezone - Timezone de l'utilisateur
 * @param {boolean} showAllTime - Afficher toute la progression depuis le début au lieu de la semaine uniquement
 * @param {string} startDate - Date de début pour récupérer les données (format YYYY-MM-DD)
 * @param {string} displayMode - Mode d'affichage : "auto" (agrégation automatique) ou "daily" (jour par jour)
 * @param {boolean} showStats - Afficher les statistiques détaillées (moyenne, série, etc.) - défaut: false
 */
export default function XpDashboard({
    users = [],
    className = "",
    userTimezone = null,
    showAllTime = false,
    startDate = null,
    displayMode = "auto",
    showStats = false,
}) {
    const t = useTranslations();
    const locale = useLocale();
    // États
    const [usersData, setUsersData] = useState([]);
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [showDetails, setShowDetails] = useState(
        // Charger la préférence depuis localStorage au montage
        () => readLocal("xpDashboard_showDetails") === "true",
    );

    // Session pour détecter l'utilisateur connecté
    const { data: session } = useSession();

    // Sauvegarder la préférence dans localStorage quand elle change
    useEffect(() => {
        writeLocal("xpDashboard_showDetails", showDetails.toString());
    }, [showDetails]);

    // Ref pour le container à exporter
    const chartContainerRef = useRef(null);

    // Mémoriser les IDs des utilisateurs pour éviter les rechargements inutiles
    const userIds = useMemo(() => {
        return users.join(",");
    }, [users]);

    // Charger les données au montage du composant
    useEffect(() => {
        if (users.length > 0) {
            loadXpData();
        }
    }, [userIds, showAllTime, startDate, displayMode]);

    /**
     * Charge les données d'XP depuis l'API pour plusieurs utilisateurs
     */
    const loadXpData = async () => {
        try {
            setLoading(true);
            setError(null);

            const result = await fetchMultipleUserXpSummary(
                users,
                userTimezone,
                showAllTime,
                startDate,
            );

            if (result.error) {
                setError(result.error);
                return;
            }

            let dayNames = [
                t("xpDashboard.dayNames.sunday"),
                t("xpDashboard.dayNames.monday"),
                t("xpDashboard.dayNames.tuesday"),
                t("xpDashboard.dayNames.wednesday"),
                t("xpDashboard.dayNames.thursday"),
                t("xpDashboard.dayNames.friday"),
                t("xpDashboard.dayNames.saturday"),
            ];

            // Traiter les données pour chaque utilisateur
            const processedUsers = result.data.map((userData) => {
                const forceDaily = displayMode === "daily";
                const transformedData = showAllTime
                    ? transformXpDataForAllTime(
                          userData.summary,
                          dayNames,
                          t("xpDashboard.dayNames.today"),
                          t("xpDashboard.dayNames.yesterday"),
                          userTimezone,
                          locale,
                          forceDaily,
                      )
                    : transformXpDataForChart(
                          userData.summary,
                          dayNames,
                          t("xpDashboard.dayNames.today"),
                          t("xpDashboard.dayNames.yesterday"),
                          userTimezone,
                      );
                const totalXp = calculateTotalXp(transformedData);

                // Déterminer le nom d'affichage
                const isCurrentUser =
                    session &&
                    session.user &&
                    session.user.userId === userData.userId;
                const displayName = isCurrentUser
                    ? t("xpDashboard.me")
                    : userData.displayName;

                return {
                    id: userData.userId,
                    name: displayName,
                    totalXp,
                    chartData: transformedData,
                    isCurrentUser,
                    error: userData.error,
                    pseudo: userData.pseudo,
                    userSlug: userData.pseudo || `believer-${userData.userId}`,
                };
            });

            setUsersData(processedUsers);

            // Préparer les données pour le graphique (combine tous les utilisateurs)
            const combinedChartData = combineUserDataForChart(processedUsers);
            setChartData(combinedChartData);
        } catch (err) {
            console.error("Erreur lors du chargement des données XP:", err);
            setError(t("xpDashboard.error"));
        } finally {
            setLoading(false);
        }
    };

    /**
     * Combine les données de tous les utilisateurs pour le graphique
     * @param {Array} processedUsers - Données traitées des utilisateurs
     * @returns {Array} - Données combinées pour le graphique
     */
    const combineUserDataForChart = (processedUsers) => {
        if (!processedUsers.length) return [];

        // Prendre comme base les données du premier utilisateur pour les jours
        const baseData = processedUsers[0].chartData;

        return baseData.map((dayData) => {
            const combinedDay = { ...dayData };

            // Ajouter les données de chaque utilisateur pour ce jour
            processedUsers.forEach((user) => {
                const userDayData = user.chartData.find(
                    (d) => d.day === dayData.day,
                );
                const xpValue = userDayData ? userDayData.xp : 0;

                if (user.isCurrentUser) {
                    combinedDay[user.name] = xpValue; // Utiliser le nom traduit "Moi"
                } else {
                    combinedDay[user.name] = xpValue;
                }
            });

            return combinedDay;
        });
    };

    /**
     * Gère le clic sur le bouton de rechargement
     */
    const handleRefresh = () => {
        loadXpData();
    };

    /**
     * Exporte le graphique en PDF
     */
    const handleExportPDF = async () => {
        if (!chartContainerRef.current) return;

        try {
            setExporting(true);

            // Import dynamique pour réduire la taille du bundle
            const html2canvas = (await import("html2canvas")).default;
            const jsPDF = (await import("jspdf")).jsPDF;

            // Capturer le contenu du graphique
            const canvas = await html2canvas(chartContainerRef.current, {
                scale: 2,
                backgroundColor: "#ffffff",
                logging: false,
            });

            // Créer le PDF
            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF({
                orientation: "landscape",
                unit: "mm",
                format: "a4",
            });

            const imgWidth = 280;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            // Ajouter le titre
            pdf.setFontSize(16);
            pdf.text(t("xpDashboard.progressionXp"), 15, 15);

            // Ajouter la date d'export
            pdf.setFontSize(10);
            pdf.text(
                t("xpDashboard.exportedOn", {
                    date: new Date().toLocaleDateString(locale),
                }),
                15,
                22,
            );

            // Ajouter l'image
            pdf.addImage(imgData, "PNG", 10, 30, imgWidth, imgHeight);

            // Télécharger le PDF
            const fileName = `progression-xp-${
                new Date().toISOString().split("T")[0]
            }.pdf`;
            pdf.save(fileName);
        } catch (error) {
            console.error("Erreur lors de l'export PDF:", error);
            alert(t("xpDashboard.exportError"));
        } finally {
            setExporting(false);
        }
    };

    // Configuration des séries pour le graphique avec couleurs modernes
    const chartSeries = usersData.map((user, index) => {
        const modernColors = [
            "#58CC02",
            "#FF9600",
            "#CE82FF",
            "#00B4D8",
            "#FF4757",
        ];

        return {
            name: user.name,
            dataKey: user.name, // Utiliser directement user.name qui contient déjà "Moi" traduit
            color: user.isCurrentUser
                ? "#9CA3AF"
                : modernColors[index % modernColors.length],
            strokeWidth: 3,
        };
    });

    return (
        <div className={`space-y-6 ${className}`}>
            {/* État de chargement avec skeleton */}
            {loading && (
                <div className="space-y-4 sm:space-y-6 animate-pulse">
                    {/* Bloc unique contenant le résumé et le graphique */}
                    <div className="bg-white dark:bg-grayDark-800 rounded-lg border border-gray-200 dark:border-grayDark-700">
                        {/* Skeleton pour la carte de résumé */}
                        <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-grayDark-700">
                            <div className="space-y-3 sm:space-y-4">
                                {/* Grille de statistiques skeleton */}
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                                    {[1, 2, 3].map((i) => (
                                        <div key={i} className="space-y-2">
                                            <div className="h-3 sm:h-4 bg-gray-200 dark:bg-grayDark-700 rounded w-16 sm:w-24"></div>
                                            <div className="h-6 sm:h-8 bg-gray-200 dark:bg-grayDark-700 rounded w-12 sm:w-16"></div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Skeleton pour le graphique */}
                        <div className="p-4 sm:p-6">
                            <div className="space-y-3 sm:space-y-4">
                                {/* Zone du graphique skeleton */}
                                <div className="h-48 sm:h-64 lg:h-96 bg-gray-100 dark:bg-grayDark-700/50 rounded relative overflow-hidden">
                                    {/* Lignes horizontales simulées */}
                                    <div className="absolute inset-0 flex flex-col justify-between p-3 sm:p-4">
                                        {[1, 2, 3, 4, 5].map((i) => (
                                            <div
                                                key={i}
                                                className="h-px bg-gray-200 dark:bg-grayDark-600"
                                            ></div>
                                        ))}
                                    </div>

                                    {/* Courbes simulées (2 couleurs différentes) */}
                                    <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 right-3 sm:right-4 flex items-end justify-between h-24 sm:h-36 lg:h-48">
                                        {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                                            <div
                                                key={i}
                                                className="relative w-4 sm:w-6 lg:w-8 space-y-1"
                                            >
                                                {/* Première courbe (gris) */}
                                                <div
                                                    className="bg-gray-300 dark:bg-grayDark-600 rounded-t w-full"
                                                    style={{
                                                        height: `${
                                                            Math.random() * 60 +
                                                            20
                                                        }%`,
                                                    }}
                                                ></div>
                                                {/* Deuxième courbe (plus claire) */}
                                                <div
                                                    className="bg-gray-400 dark:bg-grayDark-500 rounded-t w-full"
                                                    style={{
                                                        height: `${
                                                            Math.random() * 80 +
                                                            10
                                                        }%`,
                                                    }}
                                                ></div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* État d'erreur */}
            {error && !loading && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                        <svg
                            className="w-5 h-5 text-red-600 dark:text-red-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                        <div>
                            <p className="text-red-800 dark:text-red-200 font-medium">
                                {t("xpDashboard.error")}
                            </p>
                            <p className="text-red-600 dark:text-red-400 text-sm">
                                {error}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleRefresh}
                        className="mt-3 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 text-sm font-medium"
                    >
                        {t("xpDashboard.retry")}
                    </button>
                </div>
            )}

            {/* Contenu principal */}
            {!loading && !error && usersData.length > 0 && (
                <>
                    {/* Bloc unique contenant le résumé et le graphique */}
                    <div
                        ref={chartContainerRef}
                        className="bg-white dark:bg-grayDark-800 rounded-xl sm:rounded-2xl border border-gray-200 dark:border-grayDark-700"
                    >
                        {/* En-tête avec bouton d'export (visible uniquement si showStats est true) */}
                        {showStats && (
                            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-grayDark-700 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
                                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                                    {t("xpDashboard.progressionXp")}
                                </h3>
                                <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
                                    {/* Bouton Afficher/Cacher les détails */}
                                    <button
                                        onClick={() =>
                                            setShowDetails(!showDetails)
                                        }
                                        className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-grayDark-700 dark:hover:bg-grayDark-600 text-gray-700 dark:text-grayDark-200 rounded-lg text-xs sm:text-sm font-medium transition-colors"
                                    >
                                        <svg
                                            className="w-3.5 h-3.5 sm:w-4 sm:h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            {showDetails ? (
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                                                />
                                            ) : (
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.543 7-1.274 4.057-5.065 7-9.543 7-4.477 0-8.268-2.943-9.542-7z"
                                                />
                                            )}
                                        </svg>
                                        <span className="hidden xs:inline">
                                            {showDetails
                                                ? t("xpDashboard.hideDetails")
                                                : t("xpDashboard.showDetails")}
                                        </span>
                                    </button>

                                    {/* Bouton Export PDF */}
                                    <button
                                        onClick={handleExportPDF}
                                        disabled={exporting}
                                        className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg text-xs sm:text-sm font-medium transition-colors"
                                    >
                                        {exporting ? (
                                            <>
                                                <svg
                                                    className="animate-spin h-3.5 w-3.5 sm:h-4 sm:w-4"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <circle
                                                        className="opacity-25"
                                                        cx="12"
                                                        cy="12"
                                                        r="10"
                                                        stroke="currentColor"
                                                        strokeWidth="4"
                                                    ></circle>
                                                    <path
                                                        className="opacity-75"
                                                        fill="currentColor"
                                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                    ></path>
                                                </svg>
                                                <span className="hidden xs:inline">{t("xpDashboard.exporting")}</span>
                                            </>
                                        ) : (
                                            <>
                                                <svg
                                                    className="w-3.5 h-3.5 sm:w-4 sm:h-4"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                                    />
                                                </svg>
                                                <span className="hidden xs:inline">{t("xpDashboard.exportPdf")}</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Carte de résumé - Toujours visible mais avec stats détaillées conditionnelles */}
                        <div
                            className={`border-b border-gray-200 dark:border-grayDark-700 ${
                                showStats && showDetails
                                    ? "p-4 sm:p-6"
                                    : "px-4 sm:px-6 pt-4 sm:pt-6 pb-2 sm:pb-3"
                            }`}
                        >
                            <XpSummaryCard
                                users={usersData}
                                period={
                                    showAllTime
                                        ? t("xpDashboard.periodAllTime")
                                        : t("xpDashboard.periodWeek")
                                }
                                className="!bg-transparent !border-0 !p-0"
                                series={chartSeries}
                                locale={locale}
                                showDetailedStats={showStats && showDetails}
                            />
                        </div>

                        {/* Graphique */}
                        <div
                            className={
                                showStats && showDetails
                                    ? "px-3 sm:px-5 pt-3 sm:pt-5 pb-2 sm:pb-3"
                                    : "p-3 sm:p-5"
                            }
                        >
                            <XpLineChart
                                data={chartData}
                                series={chartSeries}
                                height={400}
                                locale={locale}
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
